import {
  AttachMoney,
  Inventory2,
  Percent,
  ShoppingBag,
} from "@mui/icons-material";
import { Grid } from "@mui/material";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import { table10Rows } from "features/analytics";
import {
  CHART_KEY_STORE_PRODUCT_PERFORMANCE_AVG_MARGIN,
  CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_ITEMS_SOLD,
  CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_ORDERS,
  CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_REVENUE,
} from "features/analytics/constants/chartKeys";
import {
  ProductAnalyticsCarousel,
  ProductPerformanceFilterBar,
  ProductPerformanceTable,
  ProductTrendProvider,
} from "features/products";
import {
  buildProductPerformanceAttributes,
  ProductPerformanceFilters,
} from "features/products/utils/productPerformanceFilters";
import { ProductPerformanceTab } from "features/products/utils/productPerformanceTabs";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { useMemo, useState } from "react";

function StoreProductPerformancePage() {
  const [filters, setFilters] = useState<ProductPerformanceFilters>({
    category: "",
    brand: "",
  });
  const [tab, setTab] = useState<ProductPerformanceTab>("top_sellers");

  const chartAttributes = useMemo(
    () => buildProductPerformanceAttributes(filters),
    [filters],
  );

  return (
    <ProductTrendProvider chartAttributes={chartAttributes}>
      <Grid container spacing={AnalyticsSpacing} mb={4}>
        <Grid item xs={12}>
          <ProductPerformanceFilterBar
            filters={filters}
            onChange={setFilters}
            tab={tab}
            onTabChange={setTab}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_ORDERS}
            staticAttributes={chartAttributes}
            icon={<ShoppingBag />}
            chart={{
              name: "Total Orders",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALES_COUNT_LAST],
                },
              ],
              displayOptions: {
                statTitle: "Total Orders",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_ITEMS_SOLD}
            staticAttributes={chartAttributes}
            icon={<Inventory2 />}
            chart={{
              name: "Total Items Sold",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALE_ITEMS_QUANTITY],
                },
              ],
              displayOptions: {
                statTitle: "Total Items Sold",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_PRODUCT_PERFORMANCE_TOTAL_REVENUE}
            staticAttributes={chartAttributes}
            icon={<AttachMoney />}
            chart={{
              name: "Total Revenue",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_REVENUE_LAST],
                },
              ],
              displayOptions: {
                statTitle: "Total Revenue",
              },
              comparison: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_STORE_PRODUCT_PERFORMANCE_AVG_MARGIN}
            staticAttributes={chartAttributes}
            icon={<Percent />}
            chart={{
              name: "Avg. Margin",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT],
                },
              ],
              displayOptions: {
                statTitle: "Avg. Margin",
              },
              comparison: true,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <ProductAnalyticsCarousel
            chartAttributes={chartAttributes}
            tab={tab}
          />
        </Grid>

        <Grid item xs={12} sx={{ minHeight: table10Rows }}>
          <ProductPerformanceTable chartAttributes={chartAttributes} tab={tab} />
        </Grid>
      </Grid>
    </ProductTrendProvider>
  );
}

export default StoreProductPerformancePage;
